
// Import relevant services and types
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { dateToString } from "@/utils/dateUtils";
import { mapReagentData } from "../utils/reagentMapper";

// Define result type
export interface SwapUpdateResult {
  success: boolean;
  message?: string;
  error?: string;
  redirectToMessages?: {
    userId: string;
    reagentId: string;
  };
}

/**
 * Updates the status of a swap request
 * @param requestId - The ID of the swap request to update
 * @param reagentOwnerId - The ID of the reagent owner
 * @param newStatus - The new status to set (approved, rejected, completed)
 * @param message - Optional message with the status update
 * @returns Promise with result of the operation
 */
export const updateSwapRequestStatus = async (
  requestId: string,
  reagentOwnerId: string,
  newStatus: "approved" | "rejected" | "completed",
  message?: string
): Promise<SwapUpdateResult> => {
  try {
    console.log(`Updating swap request ${requestId} to status: ${newStatus}`);
    
    // Check if the request exists
    const { data: requestData, error: requestError } = await supabase
      .from("swap_requests")
      .select(`
        *,
        reagents:reagent_id(*),
        profiles:requester_id(*)
      `)
      .eq("id", requestId)
      .single();
    
    if (requestError || !requestData) {
      console.error("Error fetching swap request:", requestError);
      return { success: false, error: "Could not find the swap request" };
    }
    
    // Update the swap request status
    const { error: updateError } = await supabase
      .from("swap_requests")
      .update({ 
        status: newStatus,
        updated_at: new Date().toISOString()
      })
      .eq("id", requestId);
    
    if (updateError) {
      console.error("Error updating swap request status:", updateError);
      return { success: false, error: "Failed to update swap request status" };
    }
    
    // Handle post-update actions based on status
    if (newStatus === "completed") {
      // Note: Credit transfer and reagent status update are now handled by database triggers
      // This ensures consistency and prevents race conditions
      console.log(`Swap request ${requestId} marked as completed. Database triggers will handle credit transfer and reagent status update.`);
    }
    
    // Send notification message if provided
    if (message) {
      const actionButton = {
        actionButton: {
          text: "View Swap Request", 
          url: `/swap-request/${requestId}`,
          type: "secondary"
        }
      };

      const { error: messageError } = await supabase
        .from("messages")
        .insert({
          sender_id: (await supabase.auth.getUser()).data.user?.id,
          recipient_id: requestData.requester_id,
          reagent_id: requestData.reagent_id,
          content: message,
          read: false,
          payload: actionButton
        });
      
      if (messageError) {
        console.error("Error sending notification message:", messageError);
      }
    }

    // Add redirect information for approved requests
    const result: SwapUpdateResult = { 
      success: true, 
      message: `Swap request ${newStatus} successfully` 
    };

    // Add redirect information for approved requests
    if (newStatus === "approved") {
      result.redirectToMessages = {
        userId: requestData.requester_id,
        reagentId: requestData.reagent_id
      };
    }

    return result;
  } catch (error) {
    console.error("Exception in updateSwapRequestStatus:", error);
    return { 
      success: false, 
      error: "An unexpected error occurred while updating the swap request" 
    };
  }
};